import React from 'react';
import get from 'lodash.get';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createFragmentContainer, graphql } from 'react-relay';

// components
import { SbSharedFollowSearchProvider } from '../SbSharedFollowSearchProvider/SbSharedFollowSearchProvider';
import { SbRespFollowSearchTopButton } from '../SbRespFollowSearchTopButton/SbRespFollowSearchTopButton';
import { SbRespFollowSearchBanner } from '../SbRespFollowSearchBanner/SbRespFollowSearchBanner';
import { SbSharedTitle } from '../SbSharedTitle/SbSharedTitle';
import { SbRespRefineMenu } from '../SbRespRefineMenu/SbRespRefineMenu';
import { SbRespPagination } from '../SbRespPagination/SbRespPagination';
import { LoadingComponent } from '../../components/global/LoadingComponent';
import { SbRespContentModule } from '../SbRespContentModule/SbRespContentModule';
import { SbSharedPageBreadcrumbs } from '../SbSharedPageBreadcrumbs/SbSharedPageBreadcrumbs';
import { SbRespSearchResultContainer } from '../SbRespSearchResultContainer/SbRespSearchResultContainer';
import { SbRespBrowseHeader } from '../SbRespBrowseHeader/SbRespBrowseHeader';
import { SbRespSortItems } from '../SbRespSortItems/SbRespSortItems';
import { SbRespPageSize } from '../SbRespPageSize/SbRespPageSize';
import { SbSharedTopTileController } from '../SbSharedTopTileController/SbSharedTopTileController';
import { SbRespCategoryTiles } from '../SbRespCategoryTiles/SbRespCategoryTiles';
import { SbRespRoomTiles } from '../SbRespRoomTiles/SbRespRoomTiles';
import { SbSharedBillboardAd } from '../SbSharedBillboardAd/SbSharedBillboardAd';
import { SbSharedSkyscraperAd } from '../SbSharedSkyscraperAd/SbSharedSkyscraperAd';
import { SbRespRecentlyViewed } from '../SbRespRecentlyViewed/SbRespRecentlyViewed';
import { SbRespNullSearchResults } from '../SbRespNullSearchResults/SbRespNullSearchResults';
import { SbSharedSearchSuggestions } from '../SbSharedSearchSuggestions/SbSharedSearchSuggestions';
import { SbSharedSearchRedirectNotice } from '../SbSharedSearchRedirectNotice/SbSharedSearchRedirectNotice';
import { SbSharedRelatedItems } from '../SbSharedRelatedItems/SbSharedRelatedItems';
import { SbSharedRelatedLinks } from '../SbSharedRelatedLinks/SbSharedRelatedLinks';
import { SbRespMoreWaysToBrowse } from '../SbRespMoreWaysToBrowse/SbRespMoreWaysToBrowse';
import { SbRespNotificationUnavailableItem } from '../SbRespNotifications/SbRespNotificationUnavailableItem/SbRespNotificationUnavailableItem';
import { SbRespTradePromoDoublePoints } from '../SbRespTradePromoDoublePoints/SbRespTradePromoDoublePoints';

// constants
import { pageTypeConstants as pageTypes } from '../../constants/pageTypeConstants';
import { FILTER_REFINE_SEARCH } from '../SbSharedRefineMenu/sbSharedRefineMenuConstants';
import { MIN_THIN_PAGE_COUNT } from '../../constants/sbConstants';
import { VERTICALS } from '../../constants/uriConstants';
const { SEARCH } = pageTypes;

// styles
import styles from './SbRespComponents.scss';

function getSearchTerm(filters, isSearchPage) {
    const searchKey = isSearchPage ? SEARCH : FILTER_REFINE_SEARCH;

    return get(filters.filter(({ name }) => name === searchKey), '[0].values[0].urlLabel');
}

function SbRespComponentsComponent(props) {
    const {
        viewer,
        currency,
        searchBrowse,
        user,
        showMeasurements,
        page,
        generatedUriRef,
        refetchInFlight,
        isClient,
        relay,
    } = props;

    const {
        displayUriRef,
        filters,
        topCategoryL1,
        displayMaxNumberOfPages,
        totalResults,
        appliedFilters,
        pageType,
    } = searchBrowse;

    const {
        isContentModuleEnabled,
        isBillboardAdEnabled,
        isMoreWaysToBrowseEnabled,
        isSkyscraperAdEnabled,
        isThinSearchBrowseEnabled,
        isFollowSearchEnabled,
        isTradeDoublePointsPromoEnabled,
    } = viewer;
    const { canShowInternalSellerName, canShowSellerName } = user || {};

    const isSearch = pageType === SEARCH;
    const isFurniture = topCategoryL1 === VERTICALS.FURNITURE;
    const canShowCreatorName = !canShowSellerName && isFurniture;
    const showNullSearchResultPage = !totalResults;
    const isThinPage = totalResults < MIN_THIN_PAGE_COUNT && isThinSearchBrowseEnabled;
    const isThinBrowsePage = !isSearch && isThinPage;
    const searchTerm = getSearchTerm(appliedFilters, isSearch);

    const sharedProps = {
        currency,
        searchBrowse,
        viewer,
        user,
        isClient,
    };

    const hasSpellCheckRedirect = get(searchBrowse, 'spellCheck.redirect.redirectFrom');
    if (showNullSearchResultPage) {
        return (
            <SbRespNullSearchResults
                user={user}
                page={page}
                viewer={viewer}
                searchTerm={searchTerm}
                uriRef={generatedUriRef}
                searchBrowse={searchBrowse}
            />
        );
    }

    return (
        <SbSharedFollowSearchProvider
            user={user}
            searchBrowse={searchBrowse}
            uriRef={generatedUriRef}
            page={page}
            searchTerm={searchTerm}
            thinTrackingEnabled={isThinPage}
        >
            {({ showFollowButton, isFollowing, followSearchValue, refetchFollow }) => (
                <React.Fragment>
                    {/* Breadcrumbs */}
                    {!isSearch && <SbSharedPageBreadcrumbs searchBrowse={searchBrowse} />}

                    {hasSpellCheckRedirect && (
                        <div className={`${styles.searchSuggestions} ${styles.collated}`}>
                            <SbSharedSearchRedirectNotice
                                className={styles.redirectNotice}
                                searchBrowse={searchBrowse}
                            />
                            <SbSharedSearchSuggestions searchBrowse={searchBrowse} />
                        </div>
                    )}

                    {/* Page Header */}
                    <div className={styles.headerContainer}>
                        <SbSharedTitle searchBrowse={searchBrowse} searchTerm={searchTerm} />
                        {isFollowSearchEnabled && (
                            <SbRespFollowSearchTopButton
                                user={user}
                                uriRef={generatedUriRef}
                                showFollowButton={showFollowButton}
                                followSearchValue={followSearchValue}
                                isFollowing={isFollowing}
                                refetchFollow={refetchFollow}
                            />
                        )}
                        {isTradeDoublePointsPromoEnabled && (
                            <SbRespTradePromoDoublePoints searchBrowse={searchBrowse} />
                        )}
                    </div>

                    {!hasSpellCheckRedirect && (
                        <SbSharedSearchSuggestions
                            className={`${styles.searchSuggestions} ${styles.regular}`}
                            searchBrowse={searchBrowse}
                        />
                    )}

                    {/* Broad Search Category Tiles */}
                    <SbSharedTopTileController
                        searchBrowse={searchBrowse}
                        categoryTiles={<SbRespCategoryTiles searchBrowse={searchBrowse} />}
                        roomTiles={<SbRespRoomTiles />}
                    />

                    <div className={styles.mainContentContainer}>
                        <div className={styles.refineMenuContainer}>
                            {/* Refine Menu */}
                            <SbRespRefineMenu
                                {...sharedProps}
                                filters={filters}
                                uriRef={generatedUriRef}
                            />

                            {/* Ad */}
                            {!isThinPage &&
                                isSkyscraperAdEnabled && (
                                    <SbSharedSkyscraperAd searchBrowse={searchBrowse} />
                                )}
                            {/* Recently Viewed */}
                            <SbRespRecentlyViewed />
                        </div>

                        <div className={styles.resultsContainer}>
                            {/* Content Module */}
                            {isContentModuleEnabled && (
                                <SbRespContentModule searchBrowse={searchBrowse} />
                            )}
                            <div className={styles.resultsHeader}>
                                <div className={styles.sortContainer}>
                                    <SbRespSortItems searchBrowse={searchBrowse} />
                                    <SbRespPageSize searchBrowse={searchBrowse} />
                                </div>
                                <SbRespBrowseHeader searchBrowse={searchBrowse} />
                            </div>

                            {/* Item Results */}
                            <SbRespSearchResultContainer
                                {...sharedProps}
                                showInternalSellerName={canShowInternalSellerName}
                                showSellerName={!!canShowSellerName}
                                showCreatorName={canShowCreatorName}
                                showMeasurements={showMeasurements}
                                relay={relay}
                            />
                            {/* Related Items */}
                            {isThinBrowsePage && (
                                <SbSharedRelatedItems
                                    searchBrowse={searchBrowse}
                                    user={user}
                                    currency={currency}
                                />
                            )}
                            {/* Pagination */}
                            <div className={styles.paginationContainer}>
                                <SbRespPagination
                                    currentPage={page}
                                    numberOfPages={displayMaxNumberOfPages}
                                    displayUriRef={displayUriRef}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Follow Search */}
                    <SbRespFollowSearchBanner
                        viewer={viewer}
                        user={user}
                        showFollowButton={showFollowButton}
                        isFollowing={isFollowing}
                        followSearchValue={followSearchValue}
                        uriRef={generatedUriRef}
                        buttonSize="large"
                        refetchFollow={refetchFollow}
                    />
                    {/* Ad */}
                    {!isThinPage &&
                        isBillboardAdEnabled && (
                            <SbSharedBillboardAd searchBrowse={searchBrowse} adWidth="desktop" />
                        )}
                    {/* Related SbSharedLinks */}
                    {isThinBrowsePage && <SbSharedRelatedLinks searchBrowse={searchBrowse} />}
                    {/*  More Ways to Browse */}
                    {isMoreWaysToBrowseEnabled && (
                        <SbRespMoreWaysToBrowse searchBrowse={searchBrowse} />
                    )}
                    {/* Overlay Loader */}
                    {refetchInFlight && <LoadingComponent />}
                    {/* Unavailable Item Modal */}
                    <SbRespNotificationUnavailableItem />
                </React.Fragment>
            )}
        </SbSharedFollowSearchProvider>
    );
}

SbRespComponentsComponent.propTypes = {
    searchBrowse: PropTypes.shape({
        displayUriRef: PropTypes.string.isRequired,
        topCategoryL1: PropTypes.string,
        filters: PropTypes.array.isRequired,
    }).isRequired,
    page: PropTypes.number,
    user: PropTypes.object,
    isClient: PropTypes.bool,
    viewer: PropTypes.object,
    refetchInFlight: PropTypes.bool,
    showMeasurements: PropTypes.bool,
    generatedUriRef: PropTypes.string,
    relay: PropTypes.object.isRequired,
    currency: PropTypes.string.isRequired,
    internalBuyLinks: PropTypes.object,
};

const mapStateToProps = state => {
    const { displayPreferences, filters } = state;

    return {
        showMeasurements: displayPreferences.showMeasurements,
        generatedUriRef: filters.generatedUriRef,
        page: filters.page,
        refetchInFlight: filters.refetchInFlight,
    };
};

export const SbRespComponents = createFragmentContainer(
    connect(mapStateToProps)(SbRespComponentsComponent),
    {
        searchBrowse: graphql`
            fragment SbRespComponents_searchBrowse on SearchBrowseQueryConnection {
                displayUriRef
                topCategoryL1
                totalResults
                displayMaxNumberOfPages
                pageType
                spellCheck {
                    newQuery
                    redirect {
                        redirectFrom
                    }
                }
                ...SbSharedSearchSuggestions_searchBrowse
                ...SbSharedSearchRedirectNotice_searchBrowse
                ...SbRespRefineMenu_searchBrowse
                ...SbRespContentModule_searchBrowse
                ...SbRespNullSearchResults_searchBrowse
                ...SbSharedPageBreadcrumbs_searchBrowse
                ...SbRespSearchResultContainer_searchBrowse
                ...SbSharedTitle_searchBrowse
                ...SbRespBrowseHeader_searchBrowse
                ...SbRespSortItems_searchBrowse
                ...SbRespPageSize_searchBrowse
                ...SbSharedTopTileController_searchBrowse
                ...SbRespCategoryTiles_searchBrowse
                ...SbSharedFollowSearchProvider_searchBrowse
                ...SbSharedBillboardAd_searchBrowse
                ...SbSharedSkyscraperAd_searchBrowse
                ...SbSharedRelatedItems_searchBrowse
                ...SbSharedRelatedLinks_searchBrowse
                ...SbRespMoreWaysToBrowse_searchBrowse
                ...SbRespTradePromoDoublePoints_searchBrowse
                filters {
                    ...SbRespRefineMenu_filters
                }
                appliedFilters {
                    name
                    values {
                        urlLabel
                    }
                }
                browseContentModule(contentModuleId: $contentModuleId) {
                    description
                }
            }
        `,
        user: graphql`
            fragment SbRespComponents_user on User {
                canShowSellerName
                canShowInternalSellerName: hasPermission(name: "VIEW_SELLER_NAME")
                ...SbRespRefineMenu_user
                ...SbRespNullSearchResults_user
                ...SbRespFollowSearchBanner_user
                ...SbRespFollowSearchTopButton_user
                ...SbRespSearchResultContainer_user
                ...SbSharedFollowSearchProvider_user
                ...SbSharedRelatedItems_user
            }
        `,
        viewer: graphql`
            fragment SbRespComponents_viewer on Viewer {
                isSkyscraperAdEnabled: isEnabledByLocale(featureName: "NON_MVP_SEARCH_BROWSE")
                isContentModuleEnabled: isEnabledByLocale(featureName: "NON_MVP_SEARCH_BROWSE")
                isBillboardAdEnabled: isEnabledByLocale(featureName: "NON_MVP_SEARCH_BROWSE")
                isThinSearchBrowseEnabled: isEnabledByLocale(featureName: "NON_MVP_SEARCH_BROWSE")
                isMoreWaysToBrowseEnabled: isEnabledByLocale(featureName: "NON_MVP_SEARCH_BROWSE")
                isFollowSearchEnabled: isEnabledByLocale(featureName: "NON_MVP_SEARCH_BROWSE")
                isTradeDoublePointsPromoEnabled: isEnabled(key: "enableTradeDoublePointsPromo")
                    @include(if: $isTrade)
                ...SbRespSearchResultContainer_viewer
                ...SbRespRefineMenu_viewer
                ...SbRespNullSearchResults_viewer
                ...SbRespFollowSearchBanner_viewer
            }
        `,
    }
);
